import React, { useMemo, useState } from "react";
import EmptyState from "../Components/EmptyState";
import {
  EntitySchema,
  EntityKind,
  BackreferenceGroup,
  Fact,
  isScalarFact,
  isArrayFact,
  FactValue,
  ScalarKind,
} from "../types";
import EntityCard from "../Cards/EntityCard";
import PendingReviewBadge from "../Components/PendingReviewBadge";
import { ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import { isScalarEmpty, SimpleValueRenderer } from "../Components/FactRenderer";
import InlineEntityCard from "../Cards/InlineEntityCard";

interface EntityRelationshipsPanelProps {
  entity: EntitySchema;
}

interface EntityGrouping {
  kind: EntityKind;
  entities: Array<{
    entity: NonNullable<EntitySchema>;
    relationshipName: string;
    relationshipType: "fact" | "backreference";
    factValue: FactValue;
    factDefinition: Fact | BackreferenceGroup;
  }>;
}

const entityKindLabels: Record<EntityKind, string> = {
  person: "People",
  company: "Companies",
  manager: "Managers",
  vehicle: "Vehicles",
  topic: "Topics",
  interaction: "Interactions",
  client: "Clients",
  portfolio: "Portfolios",
  deal: "Deals",
};

// Convert Roman numeral to number
const romanToNumber = (roman: string): number => {
  const romanMap: Record<string, number> = {
    I: 1,
    V: 5,
    X: 10,
    L: 50,
    C: 100,
    D: 500,
    M: 1000,
  };

  let result = 0;
  let prevValue = 0;

  for (let i = roman.length - 1; i >= 0; i--) {
    const currentValue = romanMap[roman[i]];
    if (currentValue < prevValue) {
      result -= currentValue;
    } else {
      result += currentValue;
    }
    prevValue = currentValue;
  }

  return result;
};

// Extract Roman numeral from end of string (e.g., "Fund VIII" -> "VIII", "Fund IIIQ" -> "III")
const extractRomanNumeral = (
  str: string
): { base: string; roman: string | null; number: number } => {
  // Find all matches of Roman numerals (as whole words)
  const regex = /\b([IVXLCDM]+)\b/gi;
  const matches: Array<{ roman: string; index: number }> = [];
  let match;

  while ((match = regex.exec(str)) !== null) {
    matches.push({
      roman: match[1].toUpperCase(),
      index: match.index!,
    });
  }

  // Use the last match found (most likely to be the fund number)
  if (matches.length > 0) {
    const lastMatch = matches[matches.length - 1];
    const base = str.substring(0, lastMatch.index).trim();
    const number = romanToNumber(lastMatch.roman);
    return { base, roman: lastMatch.roman, number };
  }

  return { base: str, roman: null, number: Infinity };
};

// Sort entities alphabetically, with Roman numerals sorted numerically
const sortEntities = <T extends { entity: { name: string } }>(
  entities: T[],
  direction: "asc" | "desc" = "asc"
): T[] => {
  return [...entities].sort((a, b) => {
    const aName = a.entity.name;
    const bName = b.entity.name;

    const aParts = extractRomanNumeral(aName);
    const bParts = extractRomanNumeral(bName);

    // First compare base names
    const baseCompare = aParts.base.localeCompare(bParts.base, undefined, {
      sensitivity: "base",
      numeric: true,
    });

    if (baseCompare !== 0) {
      return direction === "asc" ? baseCompare : -baseCompare;
    }

    // If base names are the same, compare by Roman numeral number
    if (aParts.roman && bParts.roman) {
      const numCompare = aParts.number - bParts.number;
      return direction === "asc" ? numCompare : -numCompare;
    }

    // If only one has a Roman numeral, the one without comes first
    if (aParts.roman && !bParts.roman) {
      return direction === "asc" ? 1 : -1;
    }
    if (!aParts.roman && bParts.roman) {
      return direction === "asc" ? -1 : 1;
    }

    // Neither has Roman numeral, compare full names
    const fullCompare = aName.localeCompare(bName, undefined, {
      sensitivity: "base",
      numeric: true,
    });
    return direction === "asc" ? fullCompare : -fullCompare;
  });
};

// The value kinds a backreference row can show (#14370). The backreference
// payload carries no unit, hierarchy or enum information, so a number, an
// amount, a boolean, a date or a hierarchical path would render wrong there;
// only text renders, and every other kind keeps the "(from: <field>)" form.
// A null type is the employer half of a split role (#11392), whose value is
// the person's title resolved server-side, so text too.
const TEXT_SCALAR_KINDS: ReadonlySet<ScalarKind> = new Set<ScalarKind>([
  "string",
]);

/**
 * Whether a backreference element carries a value its row shows (#14370):
 * the group's value kind is text (or null), and the value is not empty by the
 * same test the forward side uses (`isScalarEmpty`: null, undefined, or a
 * blank string). A row for one that does not shows no "Name: " stub, and the
 * Relationships tab's role lines use the same test so the group and its rows
 * agree.
 */
export const linkCarriesValue = (
  scalar: unknown,
  scalarType: ScalarKind | null
): boolean =>
  (scalarType === null || TEXT_SCALAR_KINDS.has(scalarType)) &&
  !isScalarEmpty(scalar, "string");

export const generateSubheader = (
  relationshipName: string,
  relationshipType: "fact" | "backreference",
  factValue?: FactValue,
  factDefinition?: Fact | BackreferenceGroup,
  renderValue?: boolean
): React.ReactNode => {
  // Every backreference element that carries a text value shows it on the
  // linked record's row, whatever the forward field is called or designated
  // (#14370): "relationship name: value". The designation still matters
  // server-side, where it decides whose title is resolved into the employer
  // backreference (#11392). An element with no value, or a value of a kind
  // other than text, falls through to the inverse-name rendering below.
  if (
    relationshipType === "backreference" &&
    factValue &&
    factDefinition &&
    linkCarriesValue(factValue.scalar, factDefinition.scalar_type)
  ) {
    return (
      <span className="flex items-center gap-1 min-w-0">
        <span className="flex-shrink-0">{relationshipName}: </span>
        <SimpleValueRenderer
          value={factValue.scalar}
          // An employer backreference's group carries no scalar type of its
          // own — the field is a pure link, and the scalar is the person's
          // job title resolved from the TITLE field (#11392), which must hold
          // text. A null type would fall into the unknown-type rendering
          // (amber + Sparkles icon).
          scalarType={factDefinition.scalar_type ?? "string"}
          scalarUnit={
            "scalar_unit" in factDefinition
              ? factDefinition.scalar_unit
              : undefined
          }
          isHierarchical={
            "is_hierarchical" in factDefinition
              ? factDefinition.is_hierarchical
              : false
          }
          isEnum={
            "scalar_values" in factDefinition
              ? Boolean(factDefinition.scalar_values)
              : false
          }
          factName={relationshipName}
          compact
        />
      </span>
    );
  }

  // Handle backreference display with inverse name
  if (
    relationshipType === "backreference" &&
    factDefinition &&
    "forward_name" in factDefinition
  ) {
    const backrefGroup = factDefinition as BackreferenceGroup;
    // If there's an inverse name (not null and not "INVERSE RELNAME UNSET"), show it with forward name in parentheses
    if (backrefGroup.name && backrefGroup.name !== "INVERSE RELNAME UNSET") {
      return `${backrefGroup.name} (from: ${backrefGroup.forward_name})`;
    } else {
      // Fallback to the old format if no inverse name is set
      return `from: ${backrefGroup.forward_name}`;
    }
  }

  const prefix = relationshipType === "backreference" ? "from:" : "";

  // Check if this is a composite fact (both scalar and entity present)
  if (
    factValue &&
    factValue.scalar !== null &&
    factValue.scalar !== undefined &&
    factValue.entity &&
    factDefinition
  ) {
    // For composite facts, render as "Field Name: <Composite Value>"
    return (
      <span className="flex items-center gap-1 min-w-0">
        <span className="flex-shrink-0">{relationshipName}: </span>
        <SimpleValueRenderer
          value={factValue.scalar}
          scalarType={factDefinition.scalar_type}
          scalarUnit={
            "scalar_unit" in factDefinition
              ? factDefinition.scalar_unit
              : undefined
          }
          isHierarchical={
            "is_hierarchical" in factDefinition
              ? factDefinition.is_hierarchical
              : false
          }
          isEnum={
            "scalar_values" in factDefinition
              ? Boolean(factDefinition.scalar_values)
              : false
          }
          factName={relationshipName}
          compact
        />
        {/* Conditionally render the entity too, as this is primarily used for backreferences on the relationships page,
        but this may also be used by the entity search dropdown */}
        {renderValue ? <InlineEntityCard entity={factValue.entity} /> : null}
      </span>
    );
  }

  // Check if this is a scalar-only fact (scalar present but no entity)
  if (
    factValue &&
    factValue.scalar !== null &&
    factValue.scalar !== undefined &&
    !factValue.entity &&
    factDefinition &&
    renderValue
  ) {
    return (
      <span className="flex items-center gap-1 min-w-0">
        <span className="flex-shrink-0">{relationshipName}: </span>
        <SimpleValueRenderer
          value={factValue.scalar}
          scalarType={factDefinition.scalar_type}
          scalarUnit={
            "scalar_unit" in factDefinition
              ? factDefinition.scalar_unit
              : undefined
          }
          isHierarchical={
            "is_hierarchical" in factDefinition
              ? factDefinition.is_hierarchical
              : false
          }
          isEnum={
            "scalar_values" in factDefinition
              ? Boolean(factDefinition.scalar_values)
              : false
          }
          factName={relationshipName}
          compact
        />
      </span>
    );
  }

  // Check if this is an entity-only fact (entity present but no scalar)
  if (
    factValue &&
    factValue.entity &&
    (factValue.scalar === null || factValue.scalar === undefined) &&
    renderValue
  ) {
    return (
      <span className="flex items-center gap-1 min-w-0">
        <span className="flex-shrink-0">{relationshipName}: </span>
        <InlineEntityCard entity={factValue.entity} />
      </span>
    );
  }

  // Default format
  return `${prefix} ${relationshipName}`.trim();
};

const EntityRelationshipsPanel: React.FC<EntityRelationshipsPanelProps> = ({
  entity,
}) => {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<EntityKind>>(
    new Set()
  );

  const toggleGroup = (kind: EntityKind) => {
    setCollapsedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(kind)) {
        newSet.delete(kind);
      } else {
        newSet.add(kind);
      }
      return newSet;
    });
  };

  const groupedEntities = useMemo(() => {
    const entityMap = new Map<EntityKind, EntityGrouping>();

    // Process facts first
    entity.facts.forEach((fact: Fact) => {
      if (isScalarFact(fact) && fact.entity) {
        const entityKind = fact.entity.kind;

        if (!entityMap.has(entityKind)) {
          entityMap.set(entityKind, {
            kind: entityKind,
            entities: [],
          });
        }

        const group = entityMap.get(entityKind)!;

        // Check if this entity already exists in the group
        const existingEntity = group.entities.find(
          (e) => e.entity.id === fact.entity!.id
        );

        if (!existingEntity) {
          group.entities.push({
            entity: fact.entity as NonNullable<EntitySchema>,
            relationshipName: fact.name,
            relationshipType: "fact",
            factValue: { scalar: fact.scalar, entity: fact.entity },
            factDefinition: fact,
          });
        }
      } else if (isArrayFact(fact)) {
        fact.array.forEach((factValue) => {
          if (factValue.entity) {
            const entityKind = factValue.entity.kind;

            if (!entityMap.has(entityKind)) {
              entityMap.set(entityKind, {
                kind: entityKind,
                entities: [],
              });
            }

            const group = entityMap.get(entityKind)!;

            // Check if this entity already exists in the group
            const existingEntity = group.entities.find(
              (e) => e.entity.id === factValue.entity!.id
            );

            if (!existingEntity) {
              group.entities.push({
                entity: factValue.entity as NonNullable<EntitySchema>,
                relationshipName: fact.name,
                relationshipType: "fact",
                factValue: factValue,
                factDefinition: fact,
              });
            }
          }
        });
      }
    });

    // Process backreferences second
    entity.backreferences.forEach((backref: BackreferenceGroup) => {
      backref.entities.forEach((backrefEntity) => {
        if (backrefEntity.entity) {
          const entityKind = backrefEntity.entity.kind;

          if (!entityMap.has(entityKind)) {
            entityMap.set(entityKind, {
              kind: entityKind,
              entities: [],
            });
          }

          const group = entityMap.get(entityKind)!;

          // Check if this entity already exists in the group
          const existingEntity = group.entities.find(
            (e) => e.entity.id === backrefEntity.entity!.id
          );

          if (!existingEntity) {
            group.entities.push({
              entity: backrefEntity.entity as NonNullable<EntitySchema>,
              relationshipName:
                backref.name === "INVERSE RELNAME UNSET"
                  ? backref.forward_name
                  : backref.name,
              relationshipType: "backreference",
              factValue: backrefEntity,
              factDefinition: backref,
            });
          }
        }
      });
    });

    // Sort by entity kind for consistent display
    const entityKindOrder: EntityKind[] = [
      "manager",
      "vehicle",
      "company",
      "person",
      "topic",
      "interaction",
      "client",
      "portfolio",
      "deal",
    ];
    const sortedGroups = Array.from(entityMap.values()).sort((a, b) => {
      const aIndex = entityKindOrder.indexOf(a.kind);
      const bIndex = entityKindOrder.indexOf(b.kind);
      return aIndex - bIndex;
    });

    // Sort entities within each group alphabetically (with Roman numeral handling)
    sortedGroups.forEach((group) => {
      group.entities = sortEntities(group.entities, "asc");
    });

    return sortedGroups;
  }, [entity.facts, entity.backreferences]);

  // Post-#9728 each backref entry carries two flags: outer `e.staging` is
  // the source-fact's review (link-level), and `e.entity.staging` is the
  // source entity's own review (entity-level). The per-chip badge in
  // `InlineEntityCard` lights up on either; the section badge must match
  // so an interaction that has only link-pending refs still rolls up.
  const sectionStaging = groupedEntities.some((g) =>
    g.entities.some((e) => !!e.entity?.staging || !!e.staging)
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 flex flex-col h-full min-h-0">
      <h2 className="text-xl font-semibold text-gray-900 mb-4 flex-shrink-0 flex items-center gap-2">
        Relationships
        <PendingReviewBadge
          staging={sectionStaging}
          tooltip="Pending review — one or more relationships in this section come from AI-written facts awaiting approval."
        />
      </h2>

      <div className="overflow-y-auto flex-1 min-h-0">
        {groupedEntities.length === 0 ? (
          <EmptyState message="No relationships." />
        ) : (
          <div className="flex flex-col min-h-full">
            <div className="space-y-4">
              {groupedEntities.map((group) => {
                const isCollapsed = collapsedGroups.has(group.kind);
                return (
                  <div key={group.kind}>
                    <div className="flex items-center gap-1 mb-2">
                      <div
                        className="flex items-center gap-1 cursor-pointer flex-1"
                        onClick={() => toggleGroup(group.kind)}
                      >
                        {isCollapsed ? (
                          <ChevronRightIcon className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                        )}
                        <h3 className="text-sm font-medium text-gray-700">
                          {entityKindLabels[group.kind]} (
                          {group.entities.length})
                        </h3>
                      </div>
                    </div>
                    {!isCollapsed && (
                      <div className="space-y-2">
                        {group.entities.map((item) => (
                          <EntityCard
                            key={item.entity.id}
                            entity={item.entity}
                            subheader={generateSubheader(
                              item.relationshipName,
                              item.relationshipType,
                              item.factValue,
                              item.factDefinition
                            )}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EntityRelationshipsPanel;
